import { Box, Pagination, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { TOUCH_QUERY, useViewport } from "../../utils/useViewport";

// Touch screens: 40 px page buttons.
const touchItems = {
  [`@media ${TOUCH_QUERY}`]: {
    "& .MuiPaginationItem-root": {
      minWidth: 40,
      height: 40,
      borderRadius: "20px",
    },
  },
};

// Phones: no siblings and 1 px item margins, so the widest pager (7 items,
// 294 px) fits even a 360 px screen inside the page and card padding.
const phoneItems = { "& .MuiPaginationItem-root": { mx: "1px" } };

interface ListPagerProps {
  // From usePagedList.
  page: number;
  pageCount: number;
  from: number;
  to: number;
  total: number;
  onPageChange: (page: number) => void;
  // What is counted: "Showing 21–40 of 76 events".
  itemLabel?: string;
  sx?: SxProps<Theme>;
}

// "Showing 21–40 of 76" and the page buttons of a list paged with
// usePagedList. Nothing for an empty list; no buttons for a single page.
const ListPager = ({
  page,
  pageCount,
  from,
  to,
  total,
  onPageChange,
  itemLabel,
  sx = [],
}: ListPagerProps) => {
  const { isPhone } = useViewport();

  if (total === 0) return null;

  return (
    <Box
      sx={[
        {
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Showing {from}–{to} of {total}
        {itemLabel ? ` ${itemLabel}` : ""}
      </Typography>
      {pageCount > 1 && (
        <Pagination
          page={page}
          count={pageCount}
          onChange={(_event, next) => onPageChange(next)}
          siblingCount={isPhone ? 0 : 1}
          sx={[touchItems, isPhone && phoneItems]}
        />
      )}
    </Box>
  );
};

export default ListPager;
